import {
  Badge,
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  BreadcrumbItem,
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Persona,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  BookOpenRegular,
  ClipboardTaskListLtrRegular,
  DocumentBulletListRegular,
  GaugeRegular,
  KeyRegular,
  NavigationRegular,
  PeopleRegular,
  PersonRegular,
  SettingsRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import { useAuth } from '@/hooks/useAuth';
import { useHealth } from '@/hooks/useHealth';
import { browserTimeZone } from '@/lib/format';
import { HEADER_BACKGROUND } from '@/theme';

import { GlobalSearch } from './GlobalSearch';

const RAIL_WIDTH = 228;
const RAIL_COLLAPSED = 48;

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', height: '100%' },

  header: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '4px',
    height: '48px',
    flexShrink: 0,
    backgroundColor: HEADER_BACKGROUND,
    color: '#ffffff',
    paddingRight: '8px',
  },
  headerButton: {
    color: '#ffffff',
    minWidth: '48px',
    height: '48px',
    ':hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#ffffff' },
    ':hover:active': { backgroundColor: 'rgba(255,255,255,0.16)', color: '#ffffff' },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    ...shorthands.padding('0', '8px'),
    textDecorationLine: 'none',
    color: '#ffffff',
  },
  logoPlate: { backgroundColor: '#ffffff', ...shorthands.padding('4px', '6px'), display: 'flex' },
  logo: { height: '14px', width: 'auto', display: 'block' },
  productName: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    '@media (max-width: 700px)': { display: 'none' },
  },
  headerSearch: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('0', '16px'),
    '@media (max-width: 820px)': { display: 'none' },
  },
  headerRight: { display: 'flex', alignItems: 'center', columnGap: '12px', flexShrink: 0 },

  body: { display: 'flex', minHeight: 0, flexGrow: 1 },

  rail: {
    flexShrink: 0,
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    paddingTop: '4px',
    transitionProperty: 'width',
    transitionDuration: tokens.durationNormal,
  },
  railGroupLabel: {
    ...shorthands.padding('8px', '12px', '4px'),
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  railItem: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    height: '36px',
    ...shorthands.padding('0', '12px'),
    color: tokens.colorNeutralForeground1,
    textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase300,
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: 'transparent',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  railItemActive: {
    // The portal marks the current blade with a left accent bar, not a pill.
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeftColor: tokens.colorBrandStroke1,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  railIcon: { fontSize: '18px', flexShrink: 0 },

  content: { display: 'flex', minWidth: 0, flexGrow: 1, flexDirection: 'column' },
  titleBar: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('10px', '20px', '12px'),
  },
  pageTitle: {
    ...shorthands.margin('2px', '0', '0'),
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightHero700,
  },
  main: { minWidth: 0, flexGrow: 1, overflowY: 'auto', ...shorthands.padding('16px', '20px') },
  footer: {
    flexShrink: 0,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding('6px', '20px'),
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

const NAV = [
  { to: '/', label: 'Overview', Icon: GaugeRegular, exact: true },
  { to: '/messages', label: 'Messages', Icon: DocumentBulletListRegular },
  { to: '/recipients', label: 'Recipients', Icon: PeopleRegular },
  { to: '/templates', label: 'Templates', Icon: ClipboardTaskListLtrRegular },
  { to: '/docs', label: 'Documentation', Icon: BookOpenRegular },
] as const;

const ADMIN_NAV = [
  { to: '/settings/api-keys', label: 'API keys', Icon: KeyRegular },
  { to: '/settings/users', label: 'Users', Icon: SettingsRegular },
  { to: '/audit', label: 'Audit log', Icon: ClipboardTaskListLtrRegular },
] as const;

export function Shell() {
  const styles = useStyles();
  const { user, logout, isAdmin } = useAuth();
  const health = useHealth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const title = titleFor(location.pathname);
  const healthColor = health.data ? (health.data.ok ? 'success' : 'danger') : 'informative';
  const healthLabel = health.data ? (health.data.ok ? 'Healthy' : 'Attention needed') : 'Checking…';

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <Tooltip content={collapsed ? 'Expand' : 'Collapse'} relationship="label">
          <Button
            appearance="transparent"
            icon={<NavigationRegular />}
            className={styles.headerButton}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((v) => !v)}
          />
        </Tooltip>

        <NavLink to="/" className={styles.brand} aria-label="Home">
          <span className={styles.logoPlate}>
            <img src="/logo.png" alt="Syaa" className={styles.logo} />
          </span>
          <span className={styles.productName}>Notification Service</span>
        </NavLink>

        <div className={styles.headerSearch}>
          <GlobalSearch />
        </div>

        <div className={styles.headerRight}>
          <NavLink to="/" aria-label="Service health">
            <Badge appearance="filled" color={healthColor}>
              {healthLabel}
            </Badge>
          </NavLink>

          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="transparent"
                className={styles.headerButton}
                icon={<PersonRegular />}
                aria-label="Account"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem disabled>
                  <Persona
                    name={user?.email ?? ''}
                    secondaryText={user?.role === 'admin' ? 'Administrator' : 'Operations'}
                    avatar={{ color: 'colorful' }}
                  />
                </MenuItem>
                <MenuItem icon={<BookOpenRegular />} onClick={() => void navigate('/docs')}>
                  Documentation
                </MenuItem>
                <MenuItem icon={<SignOutRegular />} onClick={() => void logout()}>
                  Sign out
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </header>

      <div className={styles.body}>
        <nav
          aria-label="Main"
          className={styles.rail}
          style={{ width: collapsed ? RAIL_COLLAPSED : RAIL_WIDTH }}
        >
          {NAV.map((item) => (
            <RailLink key={item.to} {...item} collapsed={collapsed} />
          ))}

          {isAdmin && (
            <>
              {!collapsed && <div className={styles.railGroupLabel}>Administration</div>}
              {ADMIN_NAV.map((item) => (
                <RailLink key={item.to} {...item} collapsed={collapsed} />
              ))}
            </>
          )}
        </nav>

        <div className={styles.content}>
          <div className={styles.titleBar}>
            <Breadcrumb size="small" aria-label="Breadcrumb">
              <BreadcrumbItem>
                <BreadcrumbButton onClick={() => void navigate('/')}>Home</BreadcrumbButton>
              </BreadcrumbItem>
              {title !== 'Overview' && (
                <>
                  <BreadcrumbDivider />
                  <BreadcrumbItem>
                    <BreadcrumbButton current>{title}</BreadcrumbButton>
                  </BreadcrumbItem>
                </>
              )}
            </Breadcrumb>
            <h1 className={styles.pageTitle}>{title}</h1>
          </div>

          <main className={styles.main}>
            <Outlet />
          </main>

          <footer className={styles.footer}>Times shown in {browserTimeZone}</footer>
        </div>
      </div>
    </div>
  );
}

function RailLink({
  to,
  label,
  Icon,
  exact,
  collapsed,
}: {
  to: string;
  label: string;
  Icon: typeof GaugeRegular;
  exact?: boolean;
  collapsed: boolean;
}) {
  const styles = useStyles();
  const link = (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        isActive ? `${styles.railItem} ${styles.railItemActive}` : styles.railItem
      }
    >
      <Icon className={styles.railIcon} />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip content={label} relationship="label" positioning="after">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

function titleFor(pathname: string): string {
  if (pathname === '/') return 'Overview';
  if (pathname.startsWith('/messages')) return 'Messages';
  if (pathname.startsWith('/recipients')) return 'Recipients';
  if (pathname.startsWith('/templates')) return 'Templates';
  if (pathname.startsWith('/docs')) return 'Documentation';
  if (pathname.startsWith('/settings/api-keys')) return 'API keys';
  if (pathname.startsWith('/settings/users')) return 'Users';
  if (pathname.startsWith('/audit')) return 'Audit log';
  return 'Notification Service';
}
